// 標準ライブラリ
import React from "react";
import { Routes, Route } from "react-router-dom";

// プロジェクト内モジュール
import routeSchema, { type RouteSchema } from "./pages/routes";
import GlobalWrapper from "./components/global/GlobalWrapper";
import GlobalShortcutListener from "./components/global/GlobalShortcutListener";
import RequireAuth from "./components/auth/RequireAuth";
import AuthLayout from "./components/layouts/AuthLayout";
import Invite from "./pages/invite/Invite";
import Signin from "./pages/signin/signin";
import PasswordResetRequest from "./pages/password-reset/PasswordResetRequest";
import PasswordResetSent from "./pages/password-reset/PasswordResetSent";
import PasswordResetConfirm from "./pages/password-reset/PasswordResetConfirm";
import PresenceSocketProvider from "./app/presence/PresenceSocketProvider";
import MessengerSocketProvider from "./app/messenger/MessengerSocketProvider";
import DmWorkflowToastBridge from "./components/dm/DmWorkflowToastBridge";
import SoremoToaster from "./components/toast/SoremoToaster";
import SessionSwitchGuard from "./components/session-guard/SessionSwitchGuard";
import SoremonPanel from "./components/soremon/SoremonPanel";

// スタイルファイル
import "./soremo_style.scss";

const App: React.FC = () => {
  return (
    <PresenceSocketProvider>
      <MessengerSocketProvider>
        {/* SOREMO 標準トースト（白背景 / top-right / blur フェード）。アプリ全体で
          * 1 つだけマウントする。プロジェクト画面の旧 bottom-center トーストは
          * ProjectWrapper から SoremoToaster へ移行済み。 */}
        <SoremoToaster />
        <DmWorkflowToastBridge />
        {/* 全ページ共通のキーボードショートカット（⌥1/⌥2）。UI なし */}
        <GlobalShortcutListener />
        {/* 別タブ・戻るボタンでサインイン中のアカウントが切り替わった画面を
          * そのまま操作させないためのガード。全ルート共通で 1 つだけ置く。 */}
        <SessionSwitchGuard />
        {/* AI アシスタント soremon のチャットパネル（開閉はヘッダーの soremon から）。
          * 利用可能なユーザーにだけ表示される。 */}
        <SoremonPanel />
        <Routes>
          <Route
            path="/invite"
            element={
              <GlobalWrapper isHideTopBar>
                <Invite />
              </GlobalWrapper>
            }
          />
          <Route element={<GlobalWrapper isHideTopBar><AuthLayout /></GlobalWrapper>}>
            <Route path="/signin" element={<Signin />} />
            <Route path="/password-reset">
              <Route index element={<PasswordResetRequest />} />
              <Route path="sent" element={<PasswordResetSent />} />
              <Route path=":uid/:token" element={<PasswordResetConfirm />} />
            </Route>
          </Route>
          {routeSchema.map((route: RouteSchema, index: number) => {
            // GlobalWrapper でラップし、認証必須ルートはさらに RequireAuth で保護する。
            // RequireAuth を GlobalWrapper の外側に置くことで、未認証時は
            // GlobalHeader のエラー表示に到達せず即サインインへリダイレクトする。
            // raw 指定のルート（RootSegmentDispatcher 等）はケースによって
            // 認証要否・ヘッダー表示が変わるため、コンポーネント側で自前で巻く。
            const wrapped = route.raw ? (
              route.component
            ) : (
              <GlobalWrapper isHideTopBar={route?.isHideTopBar}>
                {route.component}
              </GlobalWrapper>
            );
            const element = !route.raw && route.requireAuth ? (
              <RequireAuth>{wrapped}</RequireAuth>
            ) : (
              wrapped
            );

            // 子ルートを持つ場合はネストして <Route> を作る
            if (route.children && route.children.length > 0) {
              return (
                <Route key={index} path={route.path} element={element}>
                  {route.children.map((child: RouteSchema, cIndex: number) => (
                    <Route
                      key={cIndex}
                      path={child.path}
                      element={child.component}
                    />
                  ))}
                </Route>
              );
            }
            // 子ルートを持たない場合
            return <Route key={index} path={route.path} element={element} />;
          })}
        </Routes>
      </MessengerSocketProvider>
    </PresenceSocketProvider>
  );
};

export default App; 